import React, { useEffect, useMemo } from "react";
import {
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  useColorScheme,
  Tooltip,
  Typography,
  Button
} from "@mui/material";
import { MdOutlineArrowBack, MdSunnySnowing } from "react-icons/md";
import {
  actionBarButtonPrimaryDarkStyle,
  actionBarButtonPrimaryLightStyle
} from "../../styles";
import { IoIosHelpCircleOutline } from "react-icons/io";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CalendarViewWeekOutlinedIcon from "@mui/icons-material/CalendarViewWeekOutlined";

import { ViewType, useSchedulerStore } from "../../state/scheduler.store";
import { LanguageSelector } from "../Localization/LanguageSelector";
import { useScreenSize } from "../../hooks/useScreenSize";
import isTouchDevice from "is-touch-device";
import { useTranslation } from "react-i18next";
import { focusRingStyles } from "../../utils/accessibility";

export const Header: React.FC = () => {
  const { actions, ui, clientId, sessionData, meetingLink } =
    useSchedulerStore();
  const { mode, setMode, mode: themeVariant } = useColorScheme();
  const { isSmallScreen, isMediumScreen, isLargeScreen } = useScreenSize();
  const isTouchScreen = isTouchDevice();

  const shouldHideWeeklyView = useMemo(() => {
    return (
      !!meetingLink?.hasDynamicInterviewersFilter &&
      !sessionData?.isRescheduleFlow
    );
  }, [
    meetingLink?.hasDynamicInterviewersFilter,
    sessionData?.isRescheduleFlow
  ]);

  // Define a common style for toggle buttons with non-transparent background
  const toggleButtonStyle = {
    maxHeight: "30px",
    fontSize: "14px",
    color: themeVariant === "light" ? "#202020" : "#ffffff",
    backgroundColor: themeVariant === "light" ? "#FFFFFF" : "#2f2f2f",
    border: `1px solid ${themeVariant === "light" ? "#e0e0e0" : "#5a5a5a"}`,
    "&:hover": {
      backgroundColor: themeVariant === "light" ? "#f5f5f5" : "#3a3a3a",
      borderColor: themeVariant === "light" ? "#d0d0d0" : "#6a6a6a"
    },
    "&.Mui-selected": {
      backgroundColor: themeVariant === "light" ? "#EAEAEA" : "#383838",
      borderColor: themeVariant === "light" ? "#d0d0d0" : "#6a6a6a",
      "&:hover": {
        backgroundColor: themeVariant === "light" ? "#e0e0e0" : "#404040"
      }
    },
    ...focusRingStyles
  };

  const { t } = useTranslation();

  const hideViewSelector = useMemo(
    () => ui?.currentView === "availability-slot",
    [ui?.currentView]
  );

  const getSystemMode = () => {
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  };

  useEffect(() => {
    if (mode === "system") {
      setMode(getSystemMode());
    }
  }, [mode]);

  const changeMode = (
    _: React.MouseEvent<HTMLElement>,
    mode: string | null
  ) => {
    if (mode) {
      setMode(mode as "light" | "dark" | "system");
    }
  };

  const changeWorkView = () => {
    actions.setUiState({
      ...ui,
      workDayView: !ui?.workDayView,
      currentView: "availability-slot",
      currentStep: "initial"
    });
  };

  return (
    <Stack
      component="header"
      role="banner"
      position="fixed"
      direction="row"
      width="100%"
      justifyContent="flex-end"
      alignItems="flex-end"
      px="10px"
      py="8px"
      spacing={2}
      zIndex={100}
      {...(!isLargeScreen &&
        ui?.currentView === "month" && {
          backgroundColor: mode === "light" ? "#FFFFFF" : "#121212",
          boxShadow: `0 2px 2px 0 ${mode === "light" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)"}`
        })}
    >
      {sessionData?.isRescheduleFlow && ui?.currentStep === "initial" && (
        <Stack>
          <ToggleButton
            value="month"
            sx={toggleButtonStyle}
            aria-label={t("ROOSTER.PUBLIC-APP.BACK", { defaultValue: "Back" })}
            onClick={() => {
              actions.setCleanSession(true);
              actions.setSessionData({ ...sessionData, selectedSlot: null });
              actions.setUiState({
                currentView: "month",
                currentStep: "scheduled"
              });
            }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <MdOutlineArrowBack />
              <Typography fontSize="14px" textTransform="capitalize">
                {t("ROOSTER.PUBLIC-APP.BACK", {
                  defaultValue: "Back"
                })}
              </Typography>
            </Stack>
          </ToggleButton>
        </Stack>
      )}
      <Stack>
        {clientId && <LanguageSelector show={true} client={clientId} />}
      </Stack>
      {hideViewSelector && ui?.currentStep === "initial" && isSmallScreen && (
        <Stack>
          <Button
            variant="text"
            size="large"
            onClick={() => {
              actions.setShowTour(true);
            }}
            aria-label={t("ROOSTER.PUBLIC-APP.HELP", { defaultValue: "Help" })}
            sx={{
              ...(themeVariant === "light"
                ? {
                    ...actionBarButtonPrimaryLightStyle("text"),
                    color: "#375990"
                  }
                : actionBarButtonPrimaryDarkStyle("text")),
              backgroundColor: "transparent",
              border: "0 !important",
              margin: "0",
              minWidth: "0",
              paddingLeft: "0",
              paddingRight: "0",
              ...focusRingStyles
            }}
          >
            <IoIosHelpCircleOutline size={"22px"} />
          </Button>
        </Stack>
      )}
      {ui?.currentView === "availability-slot" &&
        ui?.currentStep === "initial" && (
          <Stack alignItems={"flex-end"}>
            <ToggleButtonGroup
              value={ui?.workDayView === true ? "work_view" : "all_day"}
              exclusive
              onChange={changeWorkView}
              sx={{ maxHeight: "30px", fontSize: "26px" }}
              aria-label={t("ROOSTER.PUBLIC-APP.VIEW_TOGGLE", {
                defaultValue: "Toggle day view"
              })}
            >
              <ToggleButton
                value="work_view"
                sx={toggleButtonStyle}
                aria-label={
                  ui?.workDayView
                    ? t("ROOSTER.PUBLIC-APP.WORK_DAY_VIEW", {
                        defaultValue: "Work Day View"
                      })
                    : t("ROOSTER.PUBLIC-APP.ALL_DAY_VIEW", {
                        defaultValue: "All Day View"
                      })
                }
                aria-pressed={ui?.workDayView}
              >
                <MdSunnySnowing fontSize="inherit" />
                {!isSmallScreen && (
                  <Typography
                    fontSize="13px"
                    textTransform="capitalize"
                    sx={{ ml: 1 }}
                  >
                    {ui?.workDayView
                      ? t("ROOSTER.PUBLIC-APP.WORK_DAY_VIEW", {
                          defaultValue: "Work Day View"
                        })
                      : t("ROOSTER.PUBLIC-APP.ALL_DAY_VIEW", {
                          defaultValue: "All Day View"
                        })}
                  </Typography>
                )}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}
      <Stack alignItems={"flex-end"}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={changeMode}
          sx={{ maxHeight: "30px", fontSize: "26px" }}
          aria-label={t("ROOSTER.PUBLIC-APP.THEME_TOGGLE", {
            defaultValue: "Toggle theme"
          })}
        >
          <ToggleButton
            value="light"
            sx={toggleButtonStyle}
            aria-label={t("ROOSTER.PUBLIC-APP.LIGHT_MODE", {
              defaultValue: "Light mode"
            })}
          >
            <LightModeOutlinedIcon fontSize="inherit" />
          </ToggleButton>
          <ToggleButton
            value="dark"
            sx={toggleButtonStyle}
            aria-label={t("ROOSTER.PUBLIC-APP.DARK_MODE", {
              defaultValue: "Dark mode"
            })}
          >
            <DarkModeOutlinedIcon fontSize="inherit" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {ui?.currentStep === "initial" && !isSmallScreen && (
        <Stack sx={{ margin: 0 }} display={!hideViewSelector ? "flex" : "none"}>
          <ToggleButtonGroup
            value={ui?.currentView}
            exclusive
            onChange={(_, m) => {
              if (!m || m === ui?.currentView) {
                return;
              }
              actions.setUiView(m as ViewType);
            }}
            sx={{ maxHeight: "30px", fontSize: "18px" }}
            aria-label={t("ROOSTER.PUBLIC-APP.CALENDAR_VIEW_TOGGLE", {
              defaultValue: "Calendar view"
            })}
          >
            {(isMediumScreen || isLargeScreen) && (
              <ToggleButton
                value="month"
                sx={{ ...toggleButtonStyle, fontSize: "inherit" }}
                aria-label={t("ROOSTER.PUBLIC-APP.MONTH_VIEW", {
                  defaultValue: "Month View"
                })}
              >
                <Tooltip
                  title={t("ROOSTER.PUBLIC-APP.MONTH_VIEW", {
                    defaultValue: "Month View"
                  })}
                  slotProps={{
                    popper: {
                      disablePortal: true
                    }
                  }}
                >
                  <CalendarTodayOutlinedIcon fontSize="inherit" />
                </Tooltip>
              </ToggleButton>
            )}
            {(isMediumScreen || isLargeScreen) && (
              <ToggleButton
                value="column"
                sx={{ ...toggleButtonStyle, fontSize: "inherit" }}
                aria-label={t("ROOSTER.PUBLIC-APP.COLUMN_VIEW", {
                  defaultValue: "Column View"
                })}
              >
                <Tooltip
                  title={t("ROOSTER.PUBLIC-APP.COLUMN_VIEW", {
                    defaultValue: "Column View"
                  })}
                  slotProps={{
                    popper: {
                      disablePortal: true
                    }
                  }}
                >
                  <CalendarViewMonthOutlinedIcon fontSize="inherit" />
                </Tooltip>
              </ToggleButton>
            )}
            {isLargeScreen && !isTouchScreen && !shouldHideWeeklyView && (
              <ToggleButton
                value="week"
                sx={{ ...toggleButtonStyle, fontSize: "inherit" }}
                aria-label={t("ROOSTER.PUBLIC-APP.WEEKLY_VIEW", {
                  defaultValue: "Weekly View"
                })}
              >
                <Tooltip
                  title={t("ROOSTER.PUBLIC-APP.WEEKLY_VIEW", {
                    defaultValue: "Weekly View"
                  })}
                  slotProps={{
                    popper: {
                      disablePortal: true
                    }
                  }}
                >
                  <CalendarViewWeekOutlinedIcon fontSize="inherit" />
                </Tooltip>
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Stack>
      )}
    </Stack>
  );
};
